import { useCallback, useMemo } from "react";
import { useTable, useTableActions } from "../TableContext";

/**
 * Hook pour gérer la sélection multiple avec Select All
 */
export const useTableSelection = <T extends { id: string | number }>() => {
  const { state } = useTable();
  const {
    selectItem,
    deselectItem,
    selectAllItems,
    clearSelection
  } = useTableActions();

  const allCurrentPageIds = useMemo(() => {
    return (state.data as T[]).map(item => item.id);
  }, [state.data]);

  const areAllSelected = useMemo(() => {
    return allCurrentPageIds.length > 0 &&
      allCurrentPageIds.every(id => state.selectedItems.has(id));
  }, [allCurrentPageIds, state.selectedItems]);

  const areSomeSelected = useMemo(() => {
    return allCurrentPageIds.some(id => state.selectedItems.has(id));
  }, [allCurrentPageIds, state.selectedItems]);

  const toggleSelectAll = useCallback(() => {
    if (areAllSelected) {
      allCurrentPageIds.forEach(id => deselectItem(id));
    } else {
      selectAllItems(allCurrentPageIds);
    }
  }, [areAllSelected, allCurrentPageIds, selectAllItems, deselectItem]);

  const toggleItem = useCallback((id: string | number) => {
    if (state.selectedItems.has(id)) {
      deselectItem(id);
    } else {
      selectItem(id);
    }
  }, [state.selectedItems, selectItem, deselectItem]);

  const isItemSelected = useCallback((id: string | number) => {
    return state.selectedItems.has(id);
  }, [state.selectedItems]);

  return {
    selectedItems: state.selectedItems,
    selectedCount: state.selectedItems.size,
    areAllSelected,
    areSomeSelected,
    toggleSelectAll,
    toggleItem,
    isItemSelected,
    clearSelection,
    selectItem,
    deselectItem
  };
};
